import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import HomePageStyles from './StudioHomePage.styled';
import StudioWrapper from '@components/Studio/StudioWrapper';
import FeatureLayout from '@components/Studio/FeatureLayout/FeatureLayout';
import EmailWebForm from '@components/Studio/EmailWebForm/EmailWebForm';
import CTALayout from '@components/Studio/CTALayout/CTALayout';
import ClaytonLogo from '@components/Shared/ClaytonLogo/ClaytonLogo';
import PostCard from '@components/Studio/PostCard/PostCard';
import PreFooterCTA from '@components/Studio/PreFooterCTA/PreFooterCTA';
import LazyLoad from '@components/Shared/LazyLoad/LazyLoad';
import ImageGrid from '@components/Studio/ImageGrid/ImageGrid';
import {
  getStudioSummary,
  getPageInfo,
  getCuratedCollectionsBrief,
} from '@services/studioService';
import { getSiteUrl } from '@utils/config';

const EmailSignupContainer = dynamic(
  () => import('@components/Shared/EmailSignupContainer/EmailSignupContainer'),
  {
    ssr: false,
  }
);

const HomePage = ({ summary, pageInfo, curatedButtons = [] }) => {
  const router = useRouter();
  const [emailSignup, setEmailSignup] = useState(false);

  const recentPosts = summary?.mostRecentPosts?.slice(0, 3) || [];
  const trendingPosts = summary?.trendingPosts?.slice(0, 3) || [];

  // We are using the content CTAs for the "essential" posts and the grouped CTAs on the page.
  // The first 3 CTAs are supposed to be displayed as "the essentials" posts. The reamining CTAs
  // (up to 4 of them, can be fewer) are shown in the grouped CTA section on the page.
  const featuredPosts = pageInfo?.contentCTAs?.slice(0, 3) || [];
  const groupedCtas = pageInfo?.contentCTAs?.slice(3, 7) || [];

  const getOgImage = () => {
    return pageInfo?.ogImage?.file?.url || `/studio/${ClaytonLogo}`;
  };

  const sectionInspiration = () => {
    const { imageGridSection = {} } = pageInfo;
    const {
      sectionHeader,
      imageSource,
      images = [],
      buttonText,
      buttonURL,
    } = imageGridSection.length > 0 ? imageGridSection[0] : {};

    return images.length > 0 ? (
      <LazyLoad offset={100} classNamePrefix="image-grid">
        <div className="image-grid">
          <ImageGrid
            imageSource={imageSource}
            sectionHeader={sectionHeader}
            images={images}
            buttonText={buttonText}
            buttonURL={buttonURL}
          />
        </div>
      </LazyLoad>
    ) : null;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setEmailSignup(true);
    }, 10000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <StudioWrapper
      key=""
      posts={summary?.allPosts}
      heroData={pageInfo}
      curatedButtons={curatedButtons}
    >
      <HomePageStyles>
        <Head>
          <title>{pageInfo.titleTag}</title>
          <meta name="description" content={pageInfo.metaDescription} />
          <link rel="canonical" href={`${getSiteUrl(router.pathname)}`} />
          <meta property="og:image" content={getOgImage()} />
        </Head>

        {emailSignup ? (
          <EmailSignupContainer
            enableShowDx={false}
            pageType="studio"
            onCloseEmailPopup={() => {
              setEmailSignup(false);
            }}
          />
        ) : null}

        <div className="constrained-width">
          {featuredPosts.length > 0 ? (
            <div>
              <h3 className="headline-essentials">The Essentials</h3>
              <FeatureLayout posts={featuredPosts} />
            </div>
          ) : (
            ''
          )}
          <h3 className="headline-trending">Trending</h3>
          <div className="posts-trending">
            {trendingPosts.map((post, i) => (
              <PostCard post={post} key={`post-trending-${i}`} />
            ))}
          </div>

          <h3 className="headline-mostrecent">Most Recent</h3>
          <div className="posts-recent">
            {recentPosts.map((post, i) => (
              <PostCard post={post} key={`post-recent-${i}`} />
            ))}
          </div>
        </div>

        <CTALayout ctas={groupedCtas} />

        {pageInfo?.imageGridSection &&
          pageInfo?.imageGridSection.length > 0 &&
          sectionInspiration()}

        {pageInfo.webForms[0] && (
          <EmailWebForm
            webFormData={pageInfo.webForms[0]}
            pageType="studio_homepage"
          />
        )}

        {pageInfo.preFooterCTA && (
          <PreFooterCTA preFooterCTA={pageInfo.preFooterCTA} />
        )}
      </HomePageStyles>
    </StudioWrapper>
  );
};

HomePage.propTypes = {
  curatedButtons: PropTypes.array,
  pageInfo: PropTypes.shape({
    contentCTAs: PropTypes.array,
    heroCTA: PropTypes.any,
    heroImage: PropTypes.any,
    imageGridSection: PropTypes.array,
    metaDescription: PropTypes.any,
    mobileHeroImage: PropTypes.any,
    ogImage: PropTypes.shape({
      file: PropTypes.shape({
        url: PropTypes.any,
      }),
    }),
    preFooterCTA: PropTypes.any,
    titleTag: PropTypes.any,
    webForms: PropTypes.any,
  }),
  summary: PropTypes.shape({
    allPosts: PropTypes.any,
    mostRecentPosts: PropTypes.array,
    trendingPosts: PropTypes.array,
  }),
};

export default HomePage;

const dpcKey = '2bZLvSlcnbLYIuIGHw7nRQ';

export async function getServerSideProps() {
  let summary, dynamicPageContent, curatedButtons;

  await Promise.all([
    getStudioSummary().then((response) => {
      summary = response;
    }),

    getPageInfo(dpcKey).then((response) => {
      dynamicPageContent = response;
    }),

    getCuratedCollectionsBrief().then((response) => {
      curatedButtons = response;
    }),
  ]);

  return { props: { summary, pageInfo: dynamicPageContent, curatedButtons } };
}
